import {
  ChangeDetectorRef,
  Directive,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { combineLatest, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserService } from '@ups/user';
import { FormResponseOwnerService } from '../../services/form-response-owner.service';
import {
  formDetailAccess,
  isOwnFormResponse,
} from '../../utils/form-detail-access';
import { DynamicPageRouteFormDetailComponent } from './dynamic-page-route-form-detail.base-component';

export type FormDetailAccessState = 'checking' | 'granted' | 'denied';

/**
 * Form detail route with Bug 37038's split gate: a new fill needs the category
 * view, an existing record also needs the open-any-record right or to be the
 * caller's own (proven by the record's createdBy, never a query param). The app
 * supplies its own security reads. The page renders nothing until access settles.
 */
@Directive()
export abstract class DynamicPageRouteFormDetailAccessComponent
  extends DynamicPageRouteFormDetailComponent
  implements OnInit
{
  readonly access = signal<FormDetailAccessState>('checking');

  private readonly owners = inject(FormResponseOwnerService);
  private readonly userService = inject(UserService);
  private readonly accessCdr = inject(ChangeDetectorRef);
  private accessCheck = 0;

  protected abstract canViewCategory(category: string): boolean;
  protected abstract canOpenAnyRecord(): boolean;

  override ngOnInit() {
    super.ngOnInit();
    // the base subscribed first, so options is already written when this runs
    combineLatest([this.activeRoute.params, this.activeRoute.queryParams])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([params, queryParams]) => {
        this.evaluateAccess(
          params?.['category'],
          queryParams?.['dynamicResponseId'] || null
        );
        this.accessCdr.markForCheck();
      });
  }

  private async evaluateAccess(
    category: string,
    dynamicResponseId: string | null
  ) {
    const check = ++this.accessCheck;
    const decision = formDetailAccess({
      canViewCategory: this.canViewCategory(category),
      isExistingRecord: !!dynamicResponseId,
      canOpenAnyRecord: this.canOpenAnyRecord(),
    });
    if (decision !== 'owner-only' || !dynamicResponseId) {
      this.access.set(decision === 'granted' ? 'granted' : 'denied');
      return;
    }
    this.access.set('checking');
    const [createdBy, callerUserId] = await Promise.all([
      this.owners.loadCreatedBy(dynamicResponseId),
      this.callerUserId(),
    ]);
    // a later route change owns the answer
    if (check !== this.accessCheck) {
      return;
    }
    this.access.set(
      isOwnFormResponse(createdBy, callerUserId) ? 'granted' : 'denied'
    );
    this.accessCdr.markForCheck();
  }

  private async callerUserId(): Promise<string | null> {
    if (this.userService.myInfo?.UserId) {
      return this.userService.myInfo.UserId;
    }
    const info = await firstValueFrom(this.userService.fetchMyInfo());
    return info?.UserId ?? null;
  }
}
